import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Package, Phone, Mail, Calendar, MapPin, Clock, Leaf, ShoppingBag, Truck, CheckCircle2, XCircle, Timer, ChevronDown, ChevronUp, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const statusConfig = {
  pending: { icon: Timer, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", label: "Pending" },
  confirmed: { icon: CheckCircle2, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", label: "Confirmed" },
  processing: { icon: Package, color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200", label: "Processing" },
  shipped: { icon: Truck, color: "text-cyan-600", bg: "bg-cyan-50", border: "border-cyan-200", label: "Shipped" },
  delivered: { icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50", border: "border-green-200", label: "Delivered" },
  cancelled: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "Cancelled" },
};

const paymentBadge = (status) =>
  status === "paid"
    ? "bg-green-100 text-green-700 border-green-200"
    : "bg-amber-100 text-amber-700 border-amber-200";

export default function ProfilePage() {
  const { user, getToken, logout, updateUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        setOrders(response.data);
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [getToken]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await axios.post(`${API}/auth/avatar`, formData, {
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" },
      });
      updateUser(res.data);
      toast.success("Profile photo updated!");
    } catch {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });

  return (
    <div className="pt-16 md:pt-20 lg:pt-24 min-h-screen bg-background" data-testid="profile-page">
      {/* Hero */}
      <section className="py-8 md:py-10 bg-praroha-forest" data-testid="profile-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center ring-2 ring-white/30 relative cursor-pointer overflow-hidden group"
              onClick={() => avatarInputRef.current?.click()}
              data-testid="avatar-upload-trigger"
            >
              {user?.avatar ? (
                <img src={user.avatar.startsWith("/api") ? `${API.replace('/api','')}${user.avatar}` : user.avatar} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="font-heading text-2xl font-bold text-white">
                  {user?.name?.[0]?.toUpperCase()}
                </span>
              )}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="w-5 h-5 text-white" />
              </div>
              {uploading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>}
              <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} data-testid="avatar-file-input" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">{user?.name}</h1>
              <p className="text-white/50 text-sm">{user?.email}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-6 md:py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="orders" className="space-y-5" data-testid="profile-tabs">
            <TabsList className="bg-praroha-cream/60 p-1 rounded-full">
              <TabsTrigger
                value="orders"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5"
                data-testid="orders-tab"
              >
                <Package className="w-4 h-4 mr-2" />
                My Orders
              </TabsTrigger>
              <TabsTrigger
                value="account"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5"
                data-testid="account-tab"
              >
                <User className="w-4 h-4 mr-2" />
                Account
              </TabsTrigger>
            </TabsList>

            {/* ========== ORDERS TAB ========== */}
            <TabsContent value="orders">
              {loading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 bg-muted animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : orders.length === 0 ? (
                <Card className="border-border/40">
                  <CardContent className="py-16 text-center">
                    <ShoppingBag className="w-14 h-14 text-muted-foreground/40 mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-gray-700 mb-1">No orders yet</h3>
                    <p className="text-muted-foreground text-sm mb-5">Start shopping fresh microgreens!</p>
                    <Link to="/products">
                      <Button className="bg-praroha-forest hover:bg-praroha-forest/90 text-white rounded-full px-6">
                        <Leaf className="w-4 h-4 mr-2" /> Browse Products
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {orders.map((order, index) => {
                    const status = statusConfig[order.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    const isExpanded = expandedOrder === order.id;

                    return (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                      >
                        <Card
                          className={`border overflow-hidden transition-all ${status.border} ${isExpanded ? "shadow-md" : "hover:shadow-sm"}`}
                          data-testid={`order-${index}`}
                        >
                          {/* Order Header - clickable */}
                          <button
                            className="w-full text-left p-4 md:p-5 flex items-center gap-4"
                            onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                          >
                            <div className={`w-10 h-10 rounded-xl ${status.bg} flex items-center justify-center shrink-0`}>
                              <StatusIcon className={`w-5 h-5 ${status.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-heading font-semibold text-gray-800">Order #{order.id?.slice(0, 8)}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${paymentBadge(order.payment_status)}`}>
                                  {order.payment_status}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(order.created_at)}</span>
                                <span>{order.items?.length} item{order.items?.length !== 1 ? "s" : ""}</span>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-heading text-lg font-bold text-praroha-forest">₹{order.total?.toFixed(0)}</p>
                              <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
                            </div>
                            <div className="shrink-0 text-gray-400">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </div>
                          </button>

                          {/* Expanded details */}
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              className="border-t border-gray-100 bg-gray-50/50"
                            >
                              <div className="p-4 md:p-5 space-y-4">
                                {/* Items */}
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Items</h4>
                                  <div className="space-y-2">
                                    {order.items?.map((item, i) => (
                                      <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                                        <div>
                                          <span className="text-sm font-medium text-gray-800">{item.product_name}</span>
                                          <span className="text-xs text-muted-foreground ml-2">x{item.quantity}</span>
                                        </div>
                                        <span className="text-sm font-semibold text-praroha-forest">₹{item.item_total?.toFixed(0)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Shipping */}
                                {order.shipping_address && (
                                  <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Shipping Address</h4>
                                    <div className="bg-white rounded-lg px-3 py-2 border border-gray-100 text-sm text-gray-700 flex items-start gap-2">
                                      <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                                      <span>
                                        {order.shipping_address.full_name && `${order.shipping_address.full_name}, `}
                                        {(order.shipping_address.address_line1 || order.shipping_address.address) && `${order.shipping_address.address_line1 || order.shipping_address.address}, `}
                                        {order.shipping_address.address_line2 && `${order.shipping_address.address_line2}, `}
                                        {order.shipping_address.city && `${order.shipping_address.city}, `}
                                        {order.shipping_address.state && `${order.shipping_address.state} `}
                                        {order.shipping_address.pincode}
                                        {order.shipping_address.country && `, ${order.shipping_address.country}`}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* ========== ACCOUNT TAB ========== */}
            <TabsContent value="account">
              <div className="grid md:grid-cols-2 gap-5">
                <Card className="border-border/40">
                  <CardContent className="p-5">
                    <h2 className="font-heading text-lg font-semibold text-praroha-forest mb-4">Personal Info</h2>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-praroha-sage/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-praroha-sage" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Full Name</p>
                          <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-praroha-sage/10 flex items-center justify-center">
                          <Mail className="w-4 h-4 text-praroha-sage" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Email</p>
                          <p className="text-sm font-medium text-gray-800">{user?.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-praroha-sage/10 flex items-center justify-center">
                          <Phone className="w-4 h-4 text-praroha-sage" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Phone</p>
                          <p className="text-sm font-medium text-gray-800">{user?.phone || "Not provided"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-praroha-sage/10 flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-praroha-sage" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Member Since</p>
                          <p className="text-sm font-medium text-gray-800">{user?.created_at ? formatDate(user.created_at) : "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/40">
                  <CardContent className="p-5">
                    <h2 className="font-heading text-lg font-semibold text-praroha-forest mb-4">Quick Stats</h2>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-praroha-cream/50 rounded-xl p-4 text-center">
                        <p className="font-heading text-2xl font-bold text-praroha-forest">{orders.length}</p>
                        <p className="text-xs text-muted-foreground mt-1">Total Orders</p>
                      </div>
                      <div className="bg-praroha-cream/50 rounded-xl p-4 text-center">
                        <p className="font-heading text-2xl font-bold text-praroha-forest">
                          ₹{orders.reduce((s, o) => s + (o.total || 0), 0).toFixed(0)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Total Spent</p>
                      </div>
                      <div className="bg-praroha-cream/50 rounded-xl p-4 text-center">
                        <p className="font-heading text-2xl font-bold text-praroha-forest">
                          {orders.filter((o) => o.status === "delivered").length}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Delivered</p>
                      </div>
                      <div className="bg-praroha-cream/50 rounded-xl p-4 text-center">
                        <p className="font-heading text-2xl font-bold text-praroha-forest">
                          {orders.filter((o) => o.status === "pending" || o.status === "confirmed").length}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">In Progress</p>
                      </div>
                    </div>
                    <div className="mt-5 pt-4 border-t border-border/40">
                      <Button
                        variant="outline"
                        className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={logout}
                        data-testid="profile-logout-btn"
                      >
                        Logout
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
